import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@mui/material";
import { useNavigate } from "react-router-dom";

import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";
import WidgetBox from "../WidgetBox";
import DashboardTile from "./DashboardTile";
import { fetchReadableDashboards } from "./dashboardAccess";
import { UseIsAdmin, UseUserDetails } from "../../../hooks/auth";

export const DASHBOARD_WIDGET_KEY = "dashboard_link";

/**
 * Where a click lands. Admins get the real builder, opened straight onto
 * this board via the Dashboards tab's deep link. Everyone else gets the
 * read-only viewer — /company-metrics is admin-only, so sending a player
 * there would put them on the unauthorized screen.
 */
export const dashboardHref = (id, isAdmin) =>
  (isAdmin ? `/company-metrics?tab=dashboards&dashboard=${id}` : `/dashboards/${id}`);

const agoOf = (iso) => {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (!Number.isFinite(mins) || mins < 0) return "";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

const metaOf = (board) => {
  const count = (board.widgets || []).length;
  const bits = [`${count} widget${count === 1 ? "" : "s"}`];
  const ago = agoOf(board.updatedAt);
  if (ago) bits.push(ago);
  return bits.join(" · ");
};

/**
 * A shortcut into one Analytics board, pinned to the home page's "My
 * Widgets" panel.
 *
 * Access is re-checked on every render rather than trusted from the pin:
 * the card only draws if the board still comes back in the player's own
 * readable list, so a board that stops being shared quietly disappears
 * instead of leading to a 403.
 */
const DashboardLink = ({ widget = {}, showPerformBox, isSmallScreen = false, reFetch, setReFetch }) => {
  const navigate = useNavigate();
  const user = UseUserDetails();
  const isAdmin = UseIsAdmin();
  const [board, setBoard] = useState(undefined); // undefined = still looking

  const dashboardId = widget.dashboardId ? String(widget.dashboardId) : "";

  useEffect(() => {
    if (!showPerformBox || !dashboardId) return undefined;
    let alive = true;
    fetchReadableDashboards().then((rows) => {
      if (!alive) return;
      /* A null list means the lookup failed, not that access was revoked —
         keep whatever we were showing rather than blinking the card out. */
      if (rows === null) return;
      setBoard(rows.find((d) => String(d._id) === dashboardId) || null);
    });
    return () => {
      alive = false;
    };
  }, [showPerformBox, dashboardId]);

  /* Studio mode: the catalogue card, with a count of what's pinned so far. */
  const pinnedCount = useMemo(
    () => (user?.widgets || []).filter((w) => w.key === DASHBOARD_WIDGET_KEY).length,
    [user],
  );

  if (!showPerformBox) {
    return (
      <WidgetBox
        widget={widget}
        widgetTitle={widget.title}
        widgetMetrics={pinnedCount ? `${pinnedCount} pinned` : ""}
        reFetch={reFetch}
        setReFetch={setReFetch}
      />
    );
  }

  // Still looking, or no longer shared with this player — draw nothing.
  if (!board) return null;

  const open = () => navigate(dashboardHref(dashboardId, isAdmin));

  return (
    <Card
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      role="button"
      tabIndex={0}
      sx={{
        marginTop: isSmallScreen ? 0 : "1rem",
        boxShadow: "0px 0px 0px 0px",
        background: isSmallScreen
          ? "#F9FAFB"
          : { mini: "linear-gradient(90deg, rgba(255, 255, 255, 0.20) 0%, rgba(255, 255, 255, 0.00) 100%)", md: "#FCFCFD" },
        border: isSmallScreen ? "0.72px solid #F2F4F7" : undefined,
        borderRadius: isSmallScreen ? "6px" : undefined,
        height: isSmallScreen ? "84px" : "7rem",
        justifyContent: "center",
        transition: "box-shadow 150ms ease",
        "&:hover": {
          cursor: "pointer",
          boxShadow: "0px 2px 8px -2px rgba(16, 24, 40, 0.12)",
        },
        "&:focus-visible": {
          outline: "2px solid #B429C4",
          outlineOffset: "2px",
        },
      }}
    >
      <SoftBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { mini: "12px", md: "16px" },
          padding: { mini: "12px 16px", xxs: "16px" },
          minWidth: 0,
        }}
      >
        <DashboardTile size={isSmallScreen ? 50 : 56} />
        <SoftBox sx={{ minWidth: 0, flex: 1 }}>
          <SoftTypography
            variant="button"
            fontWeight="medium"
            sx={{
              display: "block",
              color: "#98A2B3",
              fontFamily: "DM Sans",
              fontSize: { mini: "12px", md: "14px" },
            }}
          >
            Dashboard
          </SoftTypography>
          <SoftTypography
            sx={{
              display: "block",
              color: "#0F172A",
              fontFamily: "DM Sans",
              fontSize: { mini: "16px", md: "17px" },
              fontWeight: 500,
              lineHeight: 1.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {board.name || "Untitled dashboard"}
          </SoftTypography>
          <SoftTypography
            sx={{
              display: "block",
              color: "#98A2B3",
              fontFamily: "DM Sans",
              fontSize: "12px",
              fontWeight: 500,
              marginTop: "2px",
            }}
          >
            {metaOf(board)}
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </Card>
  );
};

export default DashboardLink;
